// Inc A — Click-to-move inside-view (Matterport-style).
//
// PURE math: dato il punto di raycast (dal click sul modello) e lo stato
// camera corrente, calcola la camera target per "stare in piedi" su quel
// punto del pavimento, preservando la DIREZIONE di sguardo. Nessuna
// dipendenza da Three/orchestrator → interamente unit-testabile. Il wiring
// (pointer handler, gating pointer-lock/XR/tour, glide via flyToCamera) vive
// nell'orchestrator.
//
// NOTA surface-filter: l'adapter `raycast()` ritorna solo un punto
// (`Vector3|null`, [spark-adapter.ts:240-254]) — NESSUNA normale. Il filtro
// "pavimento" è quindi per BANDA Y attorno a floorHeight (proxy pragmatico),
// non per slope/normale. Il filtro per slope è deferito a quando l'adapter
// esporrà la normale dell'hit.

import type { CameraTarget } from '../tour/animator.js';
import { EYE_HEIGHT, FLOOR_HEIGHT } from './types.js';

export interface ClickToMoveConfig {
  /** World Y del pavimento. */
  floorHeight: number;
  /** Altezza occhi (standing) sopra il pavimento. */
  eyeHeight: number;
  /**
   * Surface-filter: scarta gli hit il cui |y - floorHeight| supera questa
   * soglia (es. pareti/soffitto/oggetti alti) → niente movimento. In metri.
   */
  maxFloorDeltaY: number;
  /** Durata del glide (ms) quando reduced-motion NON è attivo. */
  glideMs: number;
}

export const DEFAULT_CLICK_TO_MOVE: ClickToMoveConfig = {
  floorHeight: FLOOR_HEIGHT,
  eyeHeight: EYE_HEIGHT,
  maxFloorDeltaY: 1.0,
  glideMs: 600,
};

/**
 * Calcola la camera target per spostarsi sul punto cliccato.
 * Ritorna `null` se l'hit NON è un punto-pavimento (surface-filter) → il
 * chiamante non muove la camera (no-op).
 *
 * Comportamento (allineato al teleport floorplan [orchestrator.ts:991-1001]):
 *   - newPos = [hit.x, floorHeight + eyeHeight, hit.z]  (eye-height clamp)
 *   - newLookAt = newPos + (lookAt - pos)  → preserva la direzione di sguardo
 *   - fov invariato
 */
export function computeMoveTarget(
  hit: readonly [number, number, number],
  current: CameraTarget,
  cfg: ClickToMoveConfig,
): CameraTarget | null {
  if (Math.abs(hit[1] - cfg.floorHeight) > cfg.maxFloorDeltaY) {
    return null; // non è pavimento → reject
  }
  const targetY = cfg.floorHeight + cfg.eyeHeight;
  const newPos: [number, number, number] = [hit[0], targetY, hit[2]];
  const dir: [number, number, number] = [
    current.lookAt[0] - current.pos[0],
    current.lookAt[1] - current.pos[1],
    current.lookAt[2] - current.pos[2],
  ];
  const newLookAt: [number, number, number] = [
    newPos[0] + dir[0],
    newPos[1] + dir[1],
    newPos[2] + dir[2],
  ];
  return { pos: newPos, lookAt: newLookAt, fov: current.fov };
}

/** reduced-motion → 0ms (teleport istantaneo); altrimenti il glide configurato. */
export function moveDurationMs(reducedMotion: boolean, cfg: ClickToMoveConfig): number {
  return reducedMotion ? 0 : cfg.glideMs;
}
